import {
  getCompliance,
  listComplianceLocal,
  putComplianceLocal,
  deleteComplianceLocal,
  type ComplianceCategory,
  type LocalCompliance,
} from '../db/complianceDb.js';
import {
  daysRemaining,
  formatPence,
  isComplianceDue,
  nextExpiryAfterRenew,
  todayISO,
  type BillingFrequency,
} from '../lib/complianceRoll.js';
import {
  listEmptyHtml,
  listErrorHtml,
  listSkeletonHtml,
} from './listStates.js';

export type RenewalsHooks = {
  onChange: () => void;
  focusId?: string | null;
};

const CATEGORIES: ComplianceCategory[] = [
  'insurance',
  'vehicle',
  'equipment',
  'training',
  'membership',
  'statutory',
  'other',
];

const CATEGORY_LABEL: Record<ComplianceCategory, string> = {
  insurance: 'Insurance',
  vehicle: 'Vehicle',
  equipment: 'Equipment',
  training: 'Training',
  membership: 'Membership',
  statutory: 'Statutory',
  other: 'Other',
};

const FREQ_LABEL: Record<BillingFrequency, string> = {
  annual: 'Annual',
  monthly: 'Monthly',
  one_off: 'One-off',
};

/** Default lead time — 2-week renewal alert. */
const DEFAULT_LEAD_DAYS = 14;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function poundsInputToPence(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const [w, f = ''] = t.split('.');
  return Number(w) * 100 + Number((f + '00').slice(0, 2));
}

function penceToPoundsInput(p: number | null): string {
  if (p == null) return '';
  return (p / 100).toFixed(2);
}

function docBasename(ref: string | null): string | null {
  if (!ref) return null;
  const parts = ref.split('/');
  return parts[parts.length - 1] || ref;
}

function notesToListHtml(notes: string | null): string {
  if (!notes?.trim()) return '<p class="muted">No summary notes yet.</p>';
  const lines = notes
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
  return `<ul class="renew-summary-points">${lines
    .map((l) => `<li>${escapeHtml(l)}</li>`)
    .join('')}</ul>`;
}

type View = 'list' | 'edit';

export async function mountRenewals(
  root: HTMLElement,
  hooks: RenewalsHooks,
): Promise<{ refresh: () => Promise<void>; focusItem: (id: string) => void }> {
  let view: View = 'list';
  let editingId: string | null = null;
  let focusId = hooks.focusId ?? null;
  let summaryId: string | null = null;

  function focusItem(id: string) {
    focusId = id;
    summaryId = id;
    view = 'list';
    void refresh();
  }

  async function refresh() {
    if (view === 'edit') await renderForm(editingId);
    else await renderList();
  }

  function closeSummary() {
    summaryId = null;
    root.querySelector('#renew-summary-dialog')?.remove();
  }

  async function openSummary(id: string) {
    summaryId = id;
    const item = await getCompliance(id);
    if (!item) return;
    const today = todayISO();
    const due = isComplianceDue({
      expiryDate: item.expiry_date,
      leadTimeDays: item.lead_time_days,
      todayISO: today,
      active: item.active,
      deleted: !!item.deleted_at,
    });
    const rem =
      item.expiry_date != null ? daysRemaining(item.expiry_date, today) : null;
    const remLabel =
      rem == null
        ? 'No renewal date set'
        : rem < 0
          ? `${Math.abs(rem)} days overdue`
          : rem === 0
            ? 'Due today'
            : `${rem} days left`;
    const alertFrom =
      item.expiry_date != null
        ? (() => {
            const [y, m, d] = item.expiry_date.split('-').map(Number);
            const dt = new Date(Date.UTC(y!, m! - 1, d!, 12, 0, 0));
            dt.setUTCDate(dt.getUTCDate() - item.lead_time_days);
            return dt.toISOString().slice(0, 10);
          })()
        : null;
    const docName = docBasename(item.document_reference);

    root.querySelector('#renew-summary-dialog')?.remove();
    const dialog = document.createElement('div');
    dialog.id = 'renew-summary-dialog';
    dialog.className = 'renew-summary-backdrop';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'renew-summary-title');
    dialog.innerHTML = `
      <div class="renew-summary-sheet">
        <header class="renew-summary-head">
          <div>
            <p class="muted renew-summary-cat">${escapeHtml(
              CATEGORY_LABEL[item.category] ?? item.category,
            )}</p>
            <h2 id="renew-summary-title">${escapeHtml(item.title)}</h2>
          </div>
          <button type="button" class="icon-btn" data-summary-close aria-label="Close">✕</button>
        </header>
        <div class="renew-summary-status${due ? ' is-due' : ''}">
          <div>
            <span class="muted">Due</span>
            <strong>${escapeHtml(item.expiry_date ?? '—')}</strong>
            <span class="muted">${escapeHtml(remLabel)}</span>
          </div>
          <div>
            <span class="muted">Cost</span>
            <strong>${formatPence(item.cost_pence)}</strong>
            <span class="muted">${escapeHtml(
              FREQ_LABEL[item.billing_frequency] ?? item.billing_frequency,
            )}</span>
          </div>
          <div>
            <span class="muted">Alert</span>
            <strong>${item.lead_time_days}d before</strong>
            <span class="muted">${
              alertFrom ? `from ${escapeHtml(alertFrom)}` : 'no date'
            }</span>
          </div>
        </div>
        <dl class="renew-summary-meta">
          <div><dt>Provider</dt><dd>${escapeHtml(item.issuer ?? '—')}</dd></div>
          <div><dt>Reference</dt><dd>${escapeHtml(
            item.subject_reference ?? '—',
          )}</dd></div>
          ${
            item.issued_date
              ? `<div><dt>Issued / started</dt><dd>${escapeHtml(
                  item.issued_date,
                )}</dd></div>`
              : ''
          }
          ${
            docName
              ? `<div><dt>Document</dt><dd class="renew-summary-doc">${escapeHtml(
                  docName,
                )}</dd></div>`
              : ''
          }
        </dl>
        <h3 class="renew-summary-sub">Key points</h3>
        ${notesToListHtml(item.notes)}
        <div class="renew-summary-actions">
          ${
            due
              ? `<button type="button" class="btn-primary" data-summary-renew="${item.id}">Mark renewed</button>`
              : ''
          }
          <button type="button" class="btn-secondary" data-summary-edit="${item.id}">Edit</button>
          <button type="button" class="btn-secondary" data-summary-close>Close</button>
        </div>
      </div>
    `;
    root.appendChild(dialog);

    const dismiss = (ev: Event) => {
      if (ev.target === dialog) closeSummary();
    };
    dialog.addEventListener('click', dismiss);
    dialog.querySelectorAll('[data-summary-close]').forEach((el) => {
      el.addEventListener('click', () => closeSummary());
    });
    dialog.querySelector('[data-summary-edit]')?.addEventListener('click', () => {
      closeSummary();
      editingId = item.id;
      view = 'edit';
      void refresh();
    });
    dialog
      .querySelector('[data-summary-renew]')
      ?.addEventListener('click', () => {
        closeSummary();
        void markRenewed(item.id);
      });
  }

  async function renderList() {
    root.innerHTML = `
      <section class="more-panel renewals-panel">
        <button type="button" class="btn-back" data-back>‹ More</button>
        <h1 class="screen-title">Compliance</h1>
        ${listSkeletonHtml(4, 'list-skel-row skel-renew')}
      </section>
    `;
    root.querySelector('[data-back]')?.addEventListener('click', () => {
      root.dispatchEvent(new CustomEvent('renewals-back', { bubbles: true }));
    });

    const today = todayISO();
    let listError = false;
    let items: LocalCompliance[] = [];
    try {
      items = (await listComplianceLocal()).filter((c) => c.active);
    } catch {
      listError = true;
    }
    const byCat = new Map<ComplianceCategory, LocalCompliance[]>();
    for (const cat of CATEGORIES) byCat.set(cat, []);
    for (const item of items) {
      const list = byCat.get(item.category) ?? byCat.get('other')!;
      list.push(item);
    }

    const sections: string[] = [];
    for (const cat of CATEGORIES) {
      const list = byCat.get(cat) ?? [];
      if (list.length === 0) continue;
      sections.push(`
        <section class="renew-group" aria-label="${CATEGORY_LABEL[cat]}">
          <h2>${CATEGORY_LABEL[cat]}</h2>
          <ul class="renew-list">
            ${list
              .map((item) => {
                const due = isComplianceDue({
                  expiryDate: item.expiry_date,
                  leadTimeDays: item.lead_time_days,
                  todayISO: today,
                  active: item.active,
                  deleted: !!item.deleted_at,
                });
                const rem =
                  item.expiry_date != null
                    ? daysRemaining(item.expiry_date, today)
                    : null;
                const remLabel =
                  rem == null
                    ? 'No date'
                    : rem < 0
                      ? `${Math.abs(rem)}d overdue`
                      : `${rem}d left`;
                const highlight =
                  focusId === item.id || summaryId === item.id ? ' focus' : '';
                const dueClass = due ? ' due' : '';
                return `<li class="renew-item${dueClass}${highlight}" data-id="${item.id}">
                  <button type="button" class="renew-item-main" data-summary="${item.id}">
                    <strong>${escapeHtml(item.title)}</strong>
                    <span class="muted">${escapeHtml(item.issuer ?? 'No provider')} · ${
                      item.expiry_date ?? '—'
                    } · ${escapeHtml(remLabel)} · alert ${item.lead_time_days}d</span>
                    <span class="renew-cost">${formatPence(item.cost_pence)} · ${escapeHtml(
                      FREQ_LABEL[item.billing_frequency] ??
                        item.billing_frequency.replace('_', '-'),
                    )}</span>
                  </button>
                  <div class="renew-item-actions">
                    ${
                      due
                        ? `<button type="button" class="btn-primary" data-renew="${item.id}">Renewed</button>`
                        : ''
                    }
                  </div>
                </li>`;
              })
              .join('')}
          </ul>
        </section>`);
    }

    const listBody = listError
      ? listErrorHtml()
      : sections.length === 0
        ? listEmptyHtml('No compliance items yet. Add policies and certificates.', {
            label: 'Add item',
            dataAttr: 'data-add',
          })
        : sections.join('\n');

    root.innerHTML = `
      <section class="more-panel renewals-panel">
        <button type="button" class="btn-back" data-back>‹ More</button>
        <h1 class="screen-title">Compliance</h1>
        <p class="muted">Certificate and policy renewals. Costs also roll into Expenses. Tap an item for a summary; alerts appear in the bell before each due date.</p>
        ${
          !listError && sections.length > 0
            ? `<button type="button" class="btn-primary btn-block" data-add>Add item</button>`
            : ''
        }
        ${listBody}
      </section>
    `;

    root.querySelector('[data-back]')?.addEventListener('click', () => {
      root.dispatchEvent(new CustomEvent('renewals-back', { bubbles: true }));
    });
    root.querySelectorAll('[data-add]').forEach((el) => {
      el.addEventListener('click', () => {
        editingId = null;
        view = 'edit';
        void refresh();
      });
    });
    root.querySelector('[data-list-retry]')?.addEventListener('click', () => {
      void renderList();
    });
    root.querySelectorAll('[data-summary]').forEach((el) => {
      el.addEventListener('click', () => {
        void openSummary((el as HTMLElement).dataset.summary!);
      });
    });
    root.querySelectorAll('[data-renew]').forEach((el) => {
      el.addEventListener('click', () => {
        void markRenewed((el as HTMLElement).dataset.renew!);
      });
    });

    if (focusId) {
      root.querySelector(`.renew-item.focus`)?.scrollIntoView({ block: 'nearest' });
      const openId = focusId;
      focusId = null;
      void openSummary(openId);
    } else if (summaryId) {
      void openSummary(summaryId);
    }
  }

  async function markRenewed(id: string) {
    const item = await getCompliance(id);
    if (!item) return;
    const today = todayISO();
    let next: string;
    try {
      next = nextExpiryAfterRenew({
        previousExpiry: item.expiry_date,
        frequency: item.billing_frequency,
        todayISO: today,
        explicitNext: item.billing_frequency === 'one_off' ? null : null,
      });
    } catch {
      const typed = window.prompt('One-off: enter next expiry (YYYY-MM-DD)');
      if (!typed || !/^\d{4}-\d{2}-\d{2}$/.test(typed)) return;
      next = typed;
    }
    await putComplianceLocal({
      ...item,
      issued_date: today,
      expiry_date: next,
      renewNewExpiry: item.billing_frequency === 'one_off' ? next : null,
      pendingOp: 'renew',
      syncState: 'pending',
      failReason: null,
      updatedAt: new Date().toISOString(),
    });
    hooks.onChange();
    await refresh();
  }

  async function renderForm(id: string | null) {
    const existing = id ? await getCompliance(id) : null;
    const title = existing ? 'Edit compliance item' : 'Add compliance item';

    root.innerHTML = `
      <section class="more-panel">
        <button type="button" class="btn-back" data-cancel>‹ Compliance</button>
        <h1 class="screen-title">${title}</h1>
        <form id="renew-form" novalidate>
          <label class="field"><span>Title</span>
            <input name="title" required maxlength="500" value="${escapeHtml(existing?.title ?? '')}" />
          </label>
          <label class="field"><span>Category</span>
            <select name="category">
              ${CATEGORIES.map(
                (c) =>
                  `<option value="${c}" ${existing?.category === c ? 'selected' : ''}>${CATEGORY_LABEL[c]}</option>`,
              ).join('')}
            </select>
          </label>
          <label class="field"><span>Provider</span>
            <input name="issuer" maxlength="500" value="${escapeHtml(existing?.issuer ?? '')}" placeholder="Insurer, DVLA, vendor…" />
          </label>
          <label class="field"><span>Renewal date</span>
            <input type="date" name="expiry_date" value="${existing?.expiry_date ?? ''}" />
          </label>
          <label class="field"><span>Alert lead time (days)</span>
            <input type="number" name="lead_time_days" min="0" max="3650" value="${existing?.lead_time_days ?? DEFAULT_LEAD_DAYS}" />
            <span class="hint">Bell notification starts this many days before the renewal date (default 14).</span>
          </label>
          <label class="field"><span>Cost (£)</span>
            <input name="cost_pounds" inputmode="decimal" placeholder="0.00" value="${penceToPoundsInput(existing?.cost_pence ?? null)}" />
          </label>
          <label class="field"><span>Billing frequency</span>
            <select name="billing_frequency">
              ${(['annual', 'monthly', 'one_off'] as BillingFrequency[])
                .map(
                  (f) =>
                    `<option value="${f}" ${
                      (existing?.billing_frequency ?? 'annual') === f ? 'selected' : ''
                    }>${FREQ_LABEL[f]}</option>`,
                )
                .join('')}
            </select>
          </label>
          <label class="field"><span>Subject / reference</span>
            <input name="subject_reference" maxlength="500" value="${escapeHtml(existing?.subject_reference ?? '')}" placeholder="Reg plate, policy no…" />
          </label>
          <label class="field"><span>Key points / notes</span>
            <textarea name="notes" maxlength="5000" rows="8" placeholder="One point per line — shown in the summary popup">${escapeHtml(existing?.notes ?? '')}</textarea>
          </label>
          <button class="submit" type="submit">Save</button>
          ${
            existing
              ? `<button type="button" class="btn-secondary btn-block" data-delete style="margin-top:10px">Delete</button>`
              : ''
          }
        </form>
      </section>
    `;

    root.querySelector('[data-cancel]')?.addEventListener('click', () => {
      view = 'list';
      editingId = null;
      void refresh();
    });
    root.querySelector('[data-delete]')?.addEventListener('click', async () => {
      if (!existing) return;
      if (!window.confirm(`Delete “${existing.title}”?`)) return;
      if (existing.pendingOp === 'create') {
        await deleteComplianceLocal(existing.id);
      } else {
        await putComplianceLocal({
          ...existing,
          deleted_at: new Date().toISOString(),
          active: false,
          pendingOp: 'delete',
          syncState: 'pending',
          updatedAt: new Date().toISOString(),
        });
      }
      view = 'list';
      editingId = null;
      hooks.onChange();
      await refresh();
    });

    root.querySelector('#renew-form')?.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const form = ev.target as HTMLFormElement;
      const fd = new FormData(form);
      const titleVal = String(fd.get('title') ?? '').trim();
      if (!titleVal) return;
      const cost = poundsInputToPence(String(fd.get('cost_pounds') ?? ''));
      const lead = Number(fd.get('lead_time_days') ?? DEFAULT_LEAD_DAYS);
      const now = new Date().toISOString();
      const base: LocalCompliance = {
        id: existing?.id ?? crypto.randomUUID(),
        category: String(fd.get('category')) as ComplianceCategory,
        title: titleVal,
        subject_reference: String(fd.get('subject_reference') ?? '').trim() || null,
        issuer: String(fd.get('issuer') ?? '').trim() || null,
        issued_date: existing?.issued_date ?? null,
        expiry_date: String(fd.get('expiry_date') ?? '') || null,
        lead_time_days: Number.isFinite(lead) ? lead : DEFAULT_LEAD_DAYS,
        cost_pence: cost,
        billing_frequency: String(fd.get('billing_frequency')) as BillingFrequency,
        notes: String(fd.get('notes') ?? '').trim() || null,
        document_reference: existing?.document_reference ?? null,
        active: true,
        deleted_at: null,
        syncState: 'pending',
        pendingOp: existing ? (existing.pendingOp === 'create' ? 'create' : 'update') : 'create',
        renewNewExpiry: null,
        failReason: null,
        updatedAt: now,
      };
      await putComplianceLocal(base);
      view = 'list';
      editingId = null;
      hooks.onChange();
      await refresh();
    });
  }

  await refresh();
  return { refresh, focusItem };
}
